/**
 * ConsentModal — configuración granular de cookies (Ley 1581/2012 CO).
 *
 * Card 71tCkSkN — OBS F0.5 CookieConsent banner + modal.
 *
 * Modal centrado con backdrop semi-transparente y tres categorías de
 * cookies:
 *   - **Esenciales** — bloqueada (`disabled + checked`); necesarias para
 *     el funcionamiento del sitio y no admiten opt-out. Incluye el reporte de
 *     errores técnicos a Sentry, sin datos personales (card mggUrIZI).
 *   - **Analíticas** — libre; controla Sentry Performance + Session Replay,
 *     los eventos de producto, GA4 / etc.
 *   - **Marketing** — libre; controla attribution UTM / remarketing.
 *
 * El estado interno del modal se inicializa desde `initialAnalytics` /
 * `initialMarketing` en cada apertura. "Cancelar" y click en el backdrop
 * cierran sin persistir. "Guardar" persiste vía `setConsent()` y cierra.
 *
 * Renderiza mediante `createPortal` en `document.body` para escapar
 * cualquier `overflow: hidden` o `z-index` local del árbol de la ruta.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { setConsent, type ConsentValue } from '@/lib/analytics/consent';

const MODAL_Z_INDEX = 99999;

export interface ConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Valor inicial del toggle "Analíticas" al abrir el modal. */
  initialAnalytics?: ConsentValue;
  /** Valor inicial del toggle "Marketing" al abrir el modal. */
  initialMarketing?: ConsentValue;
}

function toValue(on: boolean): ConsentValue {
  return on ? 'granted' : 'denied';
}

export default function ConsentModal({
  isOpen,
  onClose,
  initialAnalytics = 'denied',
  initialMarketing = 'denied',
}: ConsentModalProps) {
  const [analytics, setAnalytics] = useState<boolean>(
    initialAnalytics === 'granted',
  );
  const [marketing, setMarketing] = useState<boolean>(
    initialMarketing === 'granted',
  );

  // Sincronizar toggles cuando el padre reabre el modal con otros defaults
  // (evita que un cambio previo quede "pegado" en un ciclo abrir → cerrar
  // → volver a abrir).
  useEffect(() => {
    if (isOpen) {
      setAnalytics(initialAnalytics === 'granted');
      setMarketing(initialMarketing === 'granted');
    }
  }, [isOpen, initialAnalytics, initialMarketing]);

  if (!isOpen) return null;

  const handleSave = (): void => {
    setConsent({
      analytics: toValue(analytics),
      marketing: toValue(marketing),
    });
    onClose();
  };

  const modalContent = (
    <div
      className="fixed inset-0 overflow-y-auto"
      style={{ position: 'fixed', inset: 0, zIndex: MODAL_Z_INDEX }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-modal-title"
    >
      <div
        className="bg-gray-500 bg-opacity-75 transition-opacity"
        style={{ position: 'fixed', inset: 0 }}
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        className="flex items-center justify-center min-h-full pt-4 px-4 pb-20 text-center sm:p-0"
        style={{ position: 'relative', zIndex: MODAL_Z_INDEX + 1 }}
      >
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3
              id="consent-modal-title"
              className="text-lg leading-6 font-medium text-gray-900"
            >
              Configurar cookies
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Elegí qué categorías querés permitir. Podés cambiar tu
              decisión más tarde desde la configuración.
            </p>

            <ul className="mt-4 space-y-4">
              <li className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Esenciales
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Necesarias para el funcionamiento del sitio. Incluyen el
                    reporte de errores técnicos, sin datos personales.
                  </p>
                </div>
                <label className="inline-flex items-center cursor-not-allowed">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    aria-label="Cookies esenciales (obligatorias)"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-60"
                  />
                </label>
              </li>

              <li className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Analíticas
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Nos ayudan a medir el rendimiento del sitio y cómo se usa
                    (métricas de rendimiento y grabación de sesión de Sentry,
                    métricas de uso).
                  </p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    aria-label="Cookies analíticas"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              </li>

              <li className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Marketing
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Permiten atribución de campañas y contenido
                    personalizado.
                  </p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    aria-label="Cookies de marketing"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              </li>
            </ul>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSave}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
